<template>
  <div class="flex justify-center items-center mt-20">
    <div class="step-wrapper">
      <div
        v-for="(step, index) in steps"
        :key="index"
        class="circle-bar"
        @click="goToStep(step)"
      >
        <FontAwesomeIcon
          v-if="step.completed"
          icon="circle-check"
          class="h-20 mr-20"
          :class="textColorClass()"
        />

        <div
          v-else
          :class="stepCircleBorderStyle(step)"
          class="circle-bar__incomplete"
        />
        <span
          class="circle-bar__text"
          :class="textModifierClass(step)"
          :role="textRole(step)"
        >
          <span :class="underlineStep(step)">
            {{ step.label }}
          </span>

          <span
            v-if="!step.completed"
            :class="stepIncompleteTextColor(step)"
            class="font-semibold inline-block pl-3"
          >{{ stepIncompleteText(step) }}</span>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import theme from '@/mixins/theme'
import DataLayer from '@/utilities/data-layer'
import * as ROUTE_PATHS from '@/constants/route-paths'
import * as STEP_LABELS from '../../../constants/step-label'

export default {
  name: 'AppProgressCircleBar',

  mixins: [theme],

  // Props
  props: {
    returningCustomer: {
      type: Boolean,
      default: false
    },

    nextStep: {
      type: String,
      default: ''
    },

    stepLabels: {
      type: Array,
      default: () => []
    }
  },

  data () {
    return {
      flinksEnabled: process.env.VUE_APP_FLINKS_ENABLED
    }
  },

  computed: {
    steps () {
      return [
        {
          label: STEP_LABELS.PERSONAL,
          completed: this.returningCustomer ? this.$store.getters.getPeronalDetailsApplicationState : true
        }, {
          label: STEP_LABELS.BANK,
          completed: this.$store.state.documents.bankStatements,
          path: process.env.VUE_APP_DISABLE_FI_SELECTOR ? ROUTE_PATHS.VERIFY_BANK : ROUTE_PATHS.FINANCIAL_SELECTOR,
          dataLayerEvent: `spring_${this.eventPrefix}_take_me_back_bank_click`,
          disabled: !this.flinksEnabled
        }, {
          label: STEP_LABELS.IDENTITY,
          completed: this.$store.state.documents.id,
          path: ROUTE_PATHS.VERIFY_IDENTITY,
          dataLayerEvent: `spring_${this.eventPrefix}_take_me_back_ID_click`
        }, {
          label: STEP_LABELS.ADDRESS,
          completed: this.$store.state.documents.poa,
          path: ROUTE_PATHS.VERIFY_ADDRESS,
          dataLayerEvent: `spring_${this.eventPrefix}_take_me_back_address_click`,
          disabled: !this.$store.state.documents.poaRequired
        }, {
          label: STEP_LABELS.INCOME,
          completed: this.$store.state.documents.proofOfIncome,
          path: ROUTE_PATHS.VERIFY_INCOME,
          dataLayerEvent: `spring_${this.eventPrefix}_take_me_back_income_click`,
          disabled: !this.$store.state.documents.poiRequired
        }, {
          label: STEP_LABELS.PAYMENT_FREQUENCY,
          completed: this.$store.state.customer.payment_frequency,
          path: ROUTE_PATHS.PAYMENT_FREQUENCY,
          dataLayerEvent: `spring_${this.eventPrefix}_take_me_back_payment_frequency_click`
        }, {
          label: STEP_LABELS.PAYMENT_DATE,
          completed: this.$store.state.customer.payment_date,
          path: ROUTE_PATHS.PAYMENT_DATE,
          dataLayerEvent: `spring_${this.eventPrefix}_take_me_back_payment_date_click`
        }
      ].filter(
        step => this.stepLabels.includes(step.label) && !step.disabled
      )
    }
  },

  methods: {
    goToStep (step) {
      if (this.completed) return
      if (this.returningCustomer) {
        return this.$router.push({
          path: this.nextStep,
          query: { ...this.$route.query }
        })
      }

      this.$store.dispatch('setLinearDocumentSteps', false)

      DataLayer.pushObject({
        event: step.dataLayerEvent
      })

      this.$router.push({
        path: step.path,
        query: { ...this.$route.query }
      })
    },

    stepCircleBorderStyle (step) {
      return (step.completed || step.label === 'Personal Details') ? this.borderColorClass() : 'border-red'
    },

    textModifierClass (step) {
      if (step.completed) return ''

      return step.label === 'Personal Details'
        ? 'circle-bar__text__pd--incomplete'
        : 'circle-bar__text--incomplete'
    },

    stepIncompleteText (step) {
      return step.label === 'Personal Details' ? 'Started' : 'Incomplete'
    },

    stepIncompleteTextColor (step) {
      return step.label === 'Personal Details' ? this.textColorClass() : 'text-red'
    },

    textRole (step) {
      return !step.completed && !this.returningCustomer ? 'button' : ''
    },

    underlineStep (step) {
      return !step.completed && !this.returningCustomer ? 'underline' : ''
    }
  }
}
</script>

<style scoped lang="scss">
@mixin circle-bar-incomplete {
  @apply block absolute rounded-5 h-full left-0 top-0 w-full opacity-10;

  content: '';
  transform: scale(1.1, 1.4);
}

.circle-bar {
  @apply flex items-center my-15 relative cursor-pointer;

  @media (max-width: 330px) {
    @apply my-25;
  }

  svg {
    @apply w-20;
  }

  &__incomplete {
    @apply bg-white border-2 w-20 h-20 rounded-full mr-20 last:m-0;
  }

  &:not(:last-child)::before {
    @apply block absolute bg-gray-300 top-[22px] left-[10px] w-px h-[20px] z-[-1];

    content: '';

    @media (max-width: 330px) {
      @apply h-[70px];
    }
  }

  &__text {
    @apply font-semibold relative flex flex-1 justify-between;

    &__pd--incomplete::before {
      @include circle-bar-incomplete;
    }

    &--incomplete::before {
      @include circle-bar-incomplete;

      @apply bg-red;
    }
  }
}

</style>
